/* ============================================================================
 * Instância HTTP unificada + autodetector de endpoints de estoque.
 * - Usa a MESMA instância de Axios de ../api (não criar outra).
 * - Interceptor injeta Authorization (Bearer <token>) de useAuthStore
 *   ou de localStorage (movatic_token | access | token).
 * ========================================================================== */

import api from '../api' // ← src/lib/api.ts
import { useAuthStore } from '@/store/authStore'
import type {
    AxiosError,
    AxiosRequestTransformer,
    InternalAxiosRequestConfig,
} from 'axios'

/** Exponha o mesmo cliente para todo o app */
export const http = api

// ------------------------- Cabeçalhos padrão -------------------------------
http.defaults.headers.common['Accept'] = 'application/json'
http.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

http.defaults.headers.post = {
    ...(http.defaults.headers.post as any),
    'Content-Type': 'application/json; charset=utf-8',
}
http.defaults.headers.put = {
    ...(http.defaults.headers.put as any),
    'Content-Type': 'application/json; charset=utf-8',
}
http.defaults.headers.patch = {
    ...(http.defaults.headers.patch as any),
    'Content-Type': 'application/json; charset=utf-8',
}

// ------- transformRequest: serializa objetos como JSON (preserva chain) ----
const prev = http.defaults.transformRequest
const chain: AxiosRequestTransformer[] = []
if (Array.isArray(prev)) chain.push(...(prev.filter(Boolean) as AxiosRequestTransformer[]))
else if (prev) chain.push(prev as AxiosRequestTransformer)

chain.unshift((data, headers) => {
    const isPlainObject =
        data != null &&
        typeof data === 'object' &&
        Object.prototype.toString.call(data) === '[object Object]'

    if (isPlainObject) {
        if (headers) headers['Content-Type'] = headers['Content-Type'] || 'application/json; charset=utf-8'
        return JSON.stringify(data)
    }
    return data
})
http.defaults.transformRequest = chain

// --------------------------- AUTH interceptor ------------------------------
const AUTH_SCHEME = (import.meta.env as any)?.VITE_AUTH_SCHEME || 'Bearer'

function readToken(): string | null {
    try {
        const st = (useAuthStore as any)?.getState?.()
        if (st?.accessToken) return st.accessToken as string
    } catch { /* ignore */ }

    for (const k of ['movatic_token', 'access', 'token']) {
        const v = localStorage.getItem(k)
        if (v) return v
    }
    return null
}

// Evita múltiplos interceptors durante HMR
const g = globalThis as any
if (g.__mv_http_req_interceptor_id != null) {
    http.interceptors.request.eject(g.__mv_http_req_interceptor_id)
}
g.__mv_http_req_interceptor_id = http.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = readToken()
        if (!token) return config
        const h: any = (config.headers ||= {} as any)
        if (typeof h.set === 'function') {
            if (!h.has('Authorization')) h.set('Authorization', `${AUTH_SCHEME} ${token}`)
        } else {
            if (!('Authorization' in h) || !h.Authorization) {
                h.Authorization = `${AUTH_SCHEME} ${token}`
            }
        }
        return config
    }
)

// ------------------------------ Helpers ------------------------------------
function normPath(p: string, ensureTrailing = true) {
    if (!p) return '/'
    if (/^https?:\/\//i.test(p)) return p // já é absoluta
    let s = p.trim()
    if (!s.startsWith('/')) s = '/' + s
    if (ensureTrailing && !s.endsWith('/')) s = s + '/'
    return s
}
function isSuccessStatus(code: number) {
    return (code >= 200 && code < 300) || code === 204 || code === 401 || code === 403
}
async function probe(path: string) {
    const url = normPath(path)
    try {
        const r = await http.options(url, { headers: { Accept: 'application/json' } })
        return isSuccessStatus(r.status)
    } catch (e) {
        const err = e as AxiosError
        const code = err.response?.status ?? 0
        if (isSuccessStatus(code)) return true
        try {
            const r2 = await http.get(url, {
                params: { page: 1, format: 'json' },
                headers: { Accept: 'application/json' },
            })
            return isSuccessStatus(r2.status)
        } catch (e2) {
            const err2 = e2 as AxiosError
            return isSuccessStatus(err2.response?.status ?? 0)
        }
    }
}

// --------- Autodetector do endpoint de estoque -----------------------------
let cachedStockPath: string | null = null
export async function resolveStockEndpoint(): Promise<string> {
    if (cachedStockPath) return cachedStockPath

    const tested: string[] = []
    const candidates: string[] = []
    const envOverride = (import.meta.env as any)?.VITE_API_STOCK_PATH as string | undefined
    if (envOverride) candidates.push(envOverride)

    candidates.push(
        '/api/inventory/stock-items/',
        '/api/inventory/stockitems/',
        '/api/inventory/stockitem/',
        '/api/inventory/stock_items/',
        '/api/inventory/stock/',
        '/api/inventory/stockitem/list/',
        '/api/inventory/stock-items/list/',
        '/api/inventory/stock_list/',
        '/api/inventory/stock-lines/'
    )

    for (const c of candidates) {
        const p = normPath(c)
        tested.push(p)
        if (await probe(p)) {
            cachedStockPath = p
            console.log('[HTTP] Stock endpoint =', p)
            return p
        }
    }
    throw new Error(`resolveStockEndpoint: Nenhum endpoint de stock encontrado (testados: ${tested.join(', ')})`)
}

// --------------------------- Facades simples --------------------------------
export async function apiGet<T>(path: string, params?: Record<string, any>) {
    const url = normPath(path, false)
    const res = await http.get<T>(url, { params, headers: { Accept: 'application/json' } })
    return res.data as any
}
export async function apiPost<T>(path: string, data?: any) {
    const url = normPath(path, false)
    const res = await http.post<T>(url, data, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json; charset=utf-8' },
    })
    return res.data as any
}
export async function apiPut<T>(path: string, data?: any) {
    const url = normPath(path, false)
    const res = await http.put<T>(url, data, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json; charset=utf-8' },
    })
    return res.data as any
}
export async function apiPatch<T>(path: string, data?: any) {
    const url = normPath(path, false)
    const res = await http.patch<T>(url, data, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json; charset=utf-8' },
    })
    return res.data as any
}
export async function apiDelete<T>(path: string) {
    const url = normPath(path, false)
    const res = await http.delete<T>(url, { headers: { Accept: 'application/json' } })
    return res.data as any
}

/** Útil para logs/diagnóstico */
export function getBaseUrl() {
    return (http.defaults.baseURL as string) || ''
}
